import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef, useEffect, useMemo } from 'react';
import { Box, buttonClasses, useTheme } from '@mui/material';
import { Clock } from 'iconsax-react';
import { HFilledTextField } from '../TextField';
import { TimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { TIME_24_HOUR } from '@application/utilities/date-formats';
import { extractTimeRaw } from '@application/utilities/date-time-formater';
import InfoIconTooltip from '../Tooltips/InfoIconTooltip';
// Formats a Dayjs as the no-timezone ISO string the backend expects for pickup times.
const toPickupTimeIso = (d) => `2000-01-01T${String(d.hour()).padStart(2, '0')}:${String(d.minute()).padStart(2, '0')}:${String(d.second()).padStart(2, '0')}.${String(d.millisecond()).padStart(3, '0')}`;
const HTimePicker = ({ handleSelectedTime, label = '', labelTooltipText, bg = 'light', value, readOnly = false }) => {
    const theme = useTheme();
    const pendingTimeRef = useRef(null);
    const [isOpen, setIsOpen] = React.useState(false);
    const getTimeValue = () => {
        if (!value)
            return null;
        if (value instanceof Date)
            return dayjs(value);
        const rawTime = extractTimeRaw(value);
        if (!rawTime)
            return null;
        const [h, m, s] = rawTime.split(':').map(Number);
        return dayjs().hour(h).minute(m).second(s).millisecond(0);
    };
    // Reset any stale in-progress selection when the form provides a new value externally.
    useEffect(() => {
        pendingTimeRef.current = null;
    }, [value]);
    const handleTimeValue = (selectedTime) => {
        if (isOpen) {
            // Picker dialog is open: buffer until the user confirms (onClose).
            pendingTimeRef.current = selectedTime;
        }
        else {
            // Arrow-key interaction on the text field: commit immediately because onClose won't fire.
            handleSelectedTime(selectedTime?.isValid() ? toPickupTimeIso(selectedTime) : null);
        }
    };
    const handlePickerClose = () => {
        setIsOpen(false);
        const pending = pendingTimeRef.current;
        pendingTimeRef.current = null;
        handleSelectedTime(pending?.isValid() ? toPickupTimeIso(pending) : null);
    };
    const handlePickerOpen = () => {
        setIsOpen(true);
        pendingTimeRef.current = getTimeValue();
    };
    const pickerProps = {
        views: ['hours', 'minutes'],
        openTo: 'hours'
    };
    const selectedBg = theme.palette.primary.darker ?? theme.palette.primary.dark;
    const styledLabel = useMemo(() => !label ? undefined : (_jsxs(Box, { display: 'inline-flex', alignItems: 'center', gap: '4px', sx: { pointerEvents: 'auto' }, children: [label, labelTooltipText && (_jsx(Box, { sx: {
                    display: 'inline-flex',
                    alignItems: 'center',
                    pointerEvents: 'auto',
                    cursor: 'pointer'
                }, children: _jsx(InfoIconTooltip, { title: labelTooltipText, size: 'small', variant: 'dark' }) }))] })), [label, labelTooltipText]);
    return (_jsx(TimePicker, { value: pendingTimeRef.current || getTimeValue(), onChange: handleTimeValue, onOpen: handlePickerOpen, onClose: handlePickerClose, desktopModeMediaQuery: '@media(min-width:9999px)', ampm: false, format: TIME_24_HOUR, readOnly: readOnly, ...pickerProps, slotProps: {
            layout: {
                sx: {
                    '& .MuiPickersLayout-actionBar': {
                        [`& .${buttonClasses.root}:first-of-type`]: {
                            border: `1px solid ${theme.palette.primary.darker}`,
                            color: theme.palette.primary.darker,
                            backgroundColor: 'transparent',
                            '&:hover': {
                                border: `1px solid ${theme.palette.primary.darker}`,
                                color: theme.palette.primary.darker,
                                backgroundColor: 'transparent'
                            }
                        },
                        [`& .${buttonClasses.root}:last-of-type`]: {
                            backgroundColor: selectedBg,
                            color: theme.palette.mode === 'light'
                                ? theme.palette.common.white
                                : theme.palette.grey[800],
                            '&:hover': {
                                backgroundColor: selectedBg
                            }
                        }
                    }
                }
            },
            popper: {
                sx: {
                    '& .MuiMenuItem-root.MuiMultiSectionDigitalClockSection-item.Mui-selected': {
                        backgroundColor: selectedBg,
                        color: theme.palette.primary.contrastText,
                        '&:hover': { backgroundColor: selectedBg }
                    }
                }
            },
            mobilePaper: {
                sx: {
                    '& .MuiMenuItem-root.MuiMultiSectionDigitalClockSection-item.Mui-selected': {
                        backgroundColor: selectedBg,
                        color: theme.palette.primary.contrastText,
                        '&:hover': { backgroundColor: selectedBg }
                    }
                }
            }
        }, slots: {
            textField: (params) => (_jsx(HFilledTextField, { ...params, label: styledLabel, bg: bg, size: 'medium', sx: { cursor: readOnly ? 'default' : 'pointer' }, fullWidth: true, placeholder: 'Select time', InputProps: {
                    ...params.InputProps,
                    readOnly: true,
                    endAdornment: (_jsx(Clock, { className: 'end', fontSize: 20, style: { marginRight: 5 }, color: bg === 'blue' ? theme.palette.action.active : theme.palette.text.primary }))
                } }))
        } }));
};
export default HTimePicker;
